import './styles/main.scss'
import { createApp, watch } from 'vue'
import { registerPlugins, registerRouter, registerSentry } from '@/plugins'
import App from './App.vue'
import AppAuthCallback from './AppAuthCallback.vue'
import AppError from './AppError.vue'
import { useAuth } from './composables/use-auth'
import { useUserPermissions } from './composables/use-user-permissions'
import { sentrySetUser } from './plugins/sentry'
import type { AxiosError } from 'axios'

const { authenticated, user, initializeAuth, checkSession } = useAuth()
const { fetchPermissions } = useUserPermissions()

function startMainApp() {
  const app = createApp(App)
  registerPlugins(app)
  registerRouter(app)
  registerSentry(app)
  app.mount('#app')

  window.addEventListener('focus', checkSession)
}

function startAppWithError(errorMessage: string) {
  const app = createApp(AppError, { errorMessage })
  registerPlugins(app)
  app.mount('#app')
}

async function initializeMainApp() {
  try {
    await fetchPermissions()
    startMainApp()
    sentrySetUser({
      id: user.value?.profile?.sub ?? '',
      name: user.value?.profile?.name ?? '',
      email: user.value?.profile?.email ?? '',
    })
  } catch (error: unknown) {
    const err = error as AxiosError
    startAppWithError(err.message)
  }
}

if (window.location.pathname === '/auth/callback') {
  const app = createApp(AppAuthCallback)
  registerPlugins(app)
  app.mount('#app')
} else {
  try {
    await initializeAuth()

    watch(
      authenticated,
      async () => {
        if (authenticated.value) {
          await initializeMainApp()
        }
      },
      {
        once: true,
        immediate: true,
      },
    )
  } catch {
    startAppWithError('Error initializing auth')
  }
}
